import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    HostBinding,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewEncapsulation,
} from '@angular/core';
import { ICheckpointCondition, ICheckpointRange } from '@fi-shared/checklist/checklist-v2';
import { AppTextService } from '@fi-shared/app-text';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { Observable, Subject } from 'rxjs';
import { FiComponentsContainer } from '@fi-shared/fi-components-container';
import {
    Checkpoint2MeasureUnitType,
    CheckpointEditorFormModelNames,
    CheckpointType,
} from '@fi-shared/checklist/checklist-enum';
import { ValidationErrors } from '@angular/forms';
import { FiCheckpoint2HelperService } from '@fi-shared/checklist/fi-checkpoint2-helper.service';

@Component({
    selector: 'fi-checkpoint-ranges-editor',
    template: `
        @if (isMeasurementType()) {
          <p class="checkpoint-edit-section-name">{{ 'checklist.checkpoint_value_ranges' | fiAppText }}</p>
          @for (checkpointRange of checkpointRanges; track checkpointRange) {
            <fi-checkpoint-range-editor
              [ngModel]="checkpointRange"
              (ngModelChange)="checkpointRangeChange()"
              [name]="CheckpointEditorFormModelNames.Ranges"
              #rangeEditorModel="ngModel"
              [measureUnitType]="measureUnitType"
              [(checkpointConditions)]="checkpointConditions"
              (checkpointConditionsChange)="onCheckpointConditionsChange($event)"
              [allCheckpointRanges]="checkpointRanges"
              (rangeRemoveClick)="confirmRangeRemove(checkpointRange)"
              [checkpointRangeValidityUpdate$]="checkpointRangeValidityUpdate$"
              />
              <!--{{ rangeEditorModel.errors | json }}-->
              @if (rangeEditorModel.errors) {
                <p class="checkpoint-range-error">
                  {{ getCheckpointRangeErrorText(rangeEditorModel.errors) }}
                </p>
              }
            }
            <div class="buttons-container add-range-button-wrapper">
              <button mat-flat-button class="secondary-button BrandedColor no-margin" (click)="addCheckpointRange()">
                {{ 'checklist.checkpoint_add_range' | fiAppText }}
              </button>
            </div>
          }
        `,
    styleUrls: ['./ranges-editor.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-checkpoint-ranges-editor' },
    providers: [{ provide: FiComponentsContainer, useExisting: FiCheckpointRangesEditorComponent }],
    standalone: false,
})
export class FiCheckpointRangesEditorComponent extends FiComponentsContainer implements OnInit, OnChanges, OnDestroy {
    @Input() checkpointRanges: ICheckpointRange[] = [];
    @Output() checkpointRangesChange = new EventEmitter<ICheckpointRange[]>();
    @Input() checkpointType: CheckpointType;
    @Input() measureUnitType: Checkpoint2MeasureUnitType;
    @Input() checkpointConditions: ICheckpointCondition[] = [];
    @Output() checkpointConditionsChange = new EventEmitter<ICheckpointCondition[]>();

    @HostBinding('class.status-type-layout') get isStatusTypeLayout() {
        return this.isStatusType();
    }
    @HostBinding('class.hidden') get isRangesEditorHidden() {
        return !this.isMeasurementType();
    }

    constructor(
        private fiCheckpoint2HelperService: FiCheckpoint2HelperService,
        private appTextService: AppTextService,
        private fiDialogsService: FiDialogsService,
        private cdr: ChangeDetectorRef,
    ) {
        super();
    }

    ngOnInit(): void {
        this.setInitialRange();
    }

    private setInitialRange() {
        if (!this.checkpointRanges.length) {
            this.checkpointRanges = [this.fiCheckpoint2HelperService.getInitialRange(this.checkpointType)];
            this.checkpointRangesChange.emit(this.checkpointRanges);
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        let change = changes.checkpointType;
        if (change && !change.firstChange) {
            // if user changes checkpoint type manually
            // we should reset ranges to initial one
            this.checkpointRanges = [];
            this.setInitialRange();
        }
    }

    isStatusType(): boolean {
        return this.checkpointType === CheckpointType.Status;
    }
    isMeasurementType(): boolean {
        return this.checkpointType === CheckpointType.Measurement;
    }

    confirmRangeRemove(rangeToRemove: ICheckpointRange) {
        this.fiDialogsService
            .confirmSimpleDelete({
                headerText: this.appTextService.get('checklist.checkpoint_range_delete') + '?',
                messageText: this.appTextService.get('checklist.checkpoint_range_delete_confirm_message'),
                yesButtonText: this.appTextService.get('checklist.checkpoint_range_delete'),
                preventUppercaseButtonLabel: true,
                config: {
                    panelClass: ['fi-confirm-delete-dialog-panel', 'fi-rounded-dialog-panel'],
                    autoFocus: false,
                },
            })
            .onYesButtonClick(() => {
                if (this.isLastRange()) {
                    this.resetRangeData();
                } else {
                    this.removeRange(rangeToRemove);
                }
            });
    }

    private _triggerCheckpointRangeValidityUpdate$ = new Subject<void>();
    checkpointRangeChange() {
        this.checkpointRangesChange.emit(this.checkpointRanges);
        this._triggerCheckpointRangeValidityUpdate$.next();
    }

    get checkpointRangeValidityUpdate$(): Observable<void> {
        return this._triggerCheckpointRangeValidityUpdate$.asObservable();
    }

    private isLastRange(): boolean {
        return this.checkpointRanges.length === 1;
    }

    private resetRangeData() {
        this.checkpointRanges = [];
        this.addCheckpointRange();
    }

    addCheckpointRange() {
        this.checkpointRanges.push(this.fiCheckpoint2HelperService.getDefaultRange());
        this.checkpointRangesChange.emit(this.checkpointRanges);
    }

    private removeRange(rangeToRemove: ICheckpointRange) {
        this.checkpointRanges.splice(this.checkpointRanges.indexOf(rangeToRemove), 1);
        this.checkpointRangesChange.emit(this.checkpointRanges);
        this.cdr.markForCheck();
    }

    onCheckpointConditionsChange($event: ICheckpointCondition[]) {
        this.checkpointConditionsChange.emit($event);
        this.cdr.markForCheck();
    }

    getCheckpointRangeErrorText(errors: ValidationErrors): string {
        if (errors.missingValues) return this.appTextService.get('common_error.mandatory');
        if (errors.rangeConflict) return this.appTextService.get('checklist.checkpoint_range_conflict_error');
        if (errors.duplicateRange) return this.appTextService.get('checklist.checkpoint_range_duplicate_range_error');
        if (errors.maxValueLessThenMinValue) return this.appTextService.get('checklist.checkpoint_range_min_max_error');
        if (errors.noOkRanges) return this.appTextService.get('checklist.checkpoint_range_no_ok_ranges_error');
    }

    ngOnDestroy$ = new Subject<void>();
    ngOnDestroy() {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }

    protected readonly CheckpointEditorFormModelNames = CheckpointEditorFormModelNames;
}
